import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fields = [
  { name: "name", label: "Votre nom", type: "text", id: "contact-name-input" },
  { name: "email", label: "Votre email", type: "email", id: "contact-email-input" },
];

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(false);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      className="px-6 md:px-12 py-32 md:py-48 border-t border-white/10"
      data-testid="contact-section"
    >
      <div className="grid md:grid-cols-2 gap-20 md:gap-32">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1, ease: [0.76, 0, 0.24, 1] }}
        >
          <p className="text-xs uppercase tracking-[0.3em] text-gold mb-8">Contact</p>
          <h2
            className="font-serif text-5xl md:text-7xl tracking-tighter leading-[0.95]"
            data-testid="contact-title"
          >
            parlons de<br />
            <span className="italic font-light text-gold">votre projet.</span>
          </h2>
          <p className="text-sm text-ash font-light leading-relaxed mt-10 max-w-sm">
            Un entretien, une réparation, un projet ? Basés à Valréas, nous
            intervenons à Valréas et dans les alentours — devis gratuit, réponse sous
            48 heures.
          </p>
          <div className="mt-10 space-y-3">
            <a
              href="tel:+33760405373"
              data-testid="contact-phone"
              className="block font-serif text-3xl md:text-4xl tracking-tight hover:text-gold transition-colors duration-500"
            >
              07 60 40 53 73
            </a>
            <a
              href="mailto:brauerelementaire@gmail.com"
              data-testid="contact-email"
              className="block text-sm text-ash hover:text-bone transition-colors duration-500"
            >
              brauerelementaire@gmail.com
            </a>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1, delay: 0.15, ease: [0.76, 0, 0.24, 1] }}
        >
          {sent ? (
            <div className="h-full flex flex-col justify-center" data-testid="contact-success">
              <p className="font-serif text-4xl md:text-5xl italic text-gold mb-6">Message envoyé.</p>
              <p className="text-sm text-ash font-light">
                Merci. Nous revenons vers vous sous 48 heures.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-12" data-testid="contact-form">
              {fields.map((f) => (
                <div key={f.name}>
                  <label
                    htmlFor={f.id}
                    className="block text-xs uppercase tracking-[0.25em] text-ash mb-3"
                  >
                    {f.label}
                  </label>
                  <input
                    id={f.id}
                    data-testid={f.id}
                    type={f.type}
                    required
                    value={form[f.name]}
                    onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                    className="w-full bg-transparent border-b border-white/20 focus:border-gold outline-none py-3 text-bone font-light transition-colors duration-500 rounded-none"
                  />
                </div>
              ))}
              <div>
                <label
                  htmlFor="contact-message-input"
                  className="block text-xs uppercase tracking-[0.25em] text-ash mb-3"
                >
                  Votre message
                </label>
                <textarea
                  id="contact-message-input"
                  data-testid="contact-message-input"
                  required
                  rows={4}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="w-full bg-transparent border-b border-white/20 focus:border-gold outline-none py-3 text-bone font-light transition-colors duration-500 resize-none rounded-none"
                />
              </div>
              {error && (
                <p className="text-xs text-red-400 tracking-wide" data-testid="contact-error">
                  Une erreur est survenue. Veuillez réessayer.
                </p>
              )}
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit-button"
                className="group relative text-xs uppercase tracking-[0.3em] text-bone hover:text-gold transition-colors duration-500 disabled:opacity-50"
              >
                {loading ? "Envoi…" : "Envoyer le message"}
                <span className="block h-px w-full bg-white/20 mt-3 overflow-hidden">
                  <span className="block h-full w-full bg-gold origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-700 ease-out" />
                </span>
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};
